/**
 * Inferred Story path: manuscript scene order → landmarks / story markers.
 * Pure domain; no Vue / canvas.
 */

import type {
  MapDocument,
  MapLandmark,
  MapSceneLink,
  MapStoryMarker,
} from "./mapDocument";
import { isJust, just, nothing, toMaybe, type Maybe } from "./maybe";
import { trimmedOrNull } from "./trimmedString";

export type StoryPathPlaceKind = "landmark" | "marker";

export type StoryPathPlace = {
  readonly kind: StoryPathPlaceKind;
  readonly id: string;
  readonly layerId: Maybe<string>;
  readonly x: number;
  readonly y: number;
  readonly sceneFiles: readonly string[];
};

export type StoryPathStop = StoryPathPlace & {
  /** 1-based index along the drawn path (after collapsing consecutive dupes). */
  readonly index: number;
};

export type StoryArcMomentRef = {
  readonly scenePath: string;
  readonly entityFile: string;
  readonly momentLabel: string;
  /** Entity accent for storyboard-style pills. */
  readonly color: Maybe<string>;
};

export type StoryPlaceHover = {
  readonly place: StoryPathPlace;
  readonly sceneFiles: readonly string[];
  readonly arcMoments: readonly StoryArcMomentRef[];
};

function sceneLinkFiles(links: Maybe<readonly MapSceneLink[]>): string[] {
  if (isJust(links) === false) return [];
  const out: string[] = [];
  for (const link of links.value) {
    const file = trimmedOrNull(link.file);
    if (file !== null) out.push(file);
  }
  return out;
}

function landmarkHasScene(landmark: MapLandmark, scenePath: string): boolean {
  return sceneLinkFiles(toMaybe(landmark.sceneLinks)).includes(scenePath);
}

function markerHasScene(marker: MapStoryMarker, scenePath: string): boolean {
  return sceneLinkFiles(toMaybe(marker.sceneLinks)).includes(scenePath);
}

/** All places on the map that reference a given manuscript scene. */
export function placesForScene(
  doc: MapDocument,
  scenePath: string,
): StoryPathPlace[] {
  const places: StoryPathPlace[] = [];
  for (const layer of doc.layers) {
    for (const landmark of layer.landmarks) {
      if (landmarkHasScene(landmark, scenePath) === false) continue;
      places.push({
        kind: "landmark",
        id: landmark.id,
        layerId: just(layer.id),
        x: landmark.x,
        y: landmark.y,
        sceneFiles: sceneLinkFiles(toMaybe(landmark.sceneLinks)),
      });
    }
  }
  for (const marker of doc.storyMarkers) {
    if (markerHasScene(marker, scenePath) === false) continue;
    places.push({
      kind: "marker",
      id: marker.id,
      layerId: nothing,
      x: marker.x,
      y: marker.y,
      sceneFiles: sceneLinkFiles(toMaybe(marker.sceneLinks)),
    });
  }
  return places;
}

/**
 * First landmark that links the scene (for sidebar icon). Free markers are
 * skipped; callers show no icon when Nothing.
 */
export function primaryLandmarkForScene(
  doc: MapDocument,
  scenePath: string,
): Maybe<MapLandmark> {
  const trimmed = trimmedOrNull(scenePath);
  if (trimmed === null) return nothing;
  for (const layer of doc.layers) {
    for (const landmark of layer.landmarks) {
      if (landmarkHasScene(landmark, trimmed)) return just(landmark);
    }
  }
  return nothing;
}

/**
 * Walk manuscript scene order and build a path of places. Scenes with no
 * place are skipped (gaps allowed). Consecutive identical place ids collapse.
 */
export function buildStoryPath(
  doc: MapDocument,
  sceneOrder: readonly string[],
): StoryPathStop[] {
  const stops: StoryPathStop[] = [];
  let lastKey = "";
  for (const scenePath of sceneOrder) {
    const trimmed = trimmedOrNull(scenePath);
    if (trimmed === null) continue;
    const places = placesForScene(doc, trimmed);
    // Prefer landmarks before free markers when a scene is on both.
    const ordered = [
      ...places.filter((p) => p.kind === "landmark"),
      ...places.filter((p) => p.kind === "marker"),
    ];
    for (const place of ordered) {
      const key = `${place.kind}:${place.id}`;
      if (key === lastKey) continue;
      lastKey = key;
      stops.push({
        ...place,
        index: stops.length + 1,
      });
    }
  }
  return stops;
}

export function findLandmarkPlace(
  doc: MapDocument,
  landmarkId: string,
): Maybe<StoryPathPlace> {
  for (const layer of doc.layers) {
    const landmark = layer.landmarks.find((lm) => lm.id === landmarkId);
    if (typeof landmark === "undefined") continue;
    return just({
      kind: "landmark",
      id: landmark.id,
      layerId: just(layer.id),
      x: landmark.x,
      y: landmark.y,
      sceneFiles: sceneLinkFiles(toMaybe(landmark.sceneLinks)),
    });
  }
  return nothing;
}

export function findMarkerPlace(
  doc: MapDocument,
  markerId: string,
): Maybe<StoryPathPlace> {
  const marker = doc.storyMarkers.find((m) => m.id === markerId);
  if (typeof marker === "undefined") return nothing;
  return just({
    kind: "marker",
    id: marker.id,
    layerId: nothing,
    x: marker.x,
    y: marker.y,
    sceneFiles: sceneLinkFiles(toMaybe(marker.sceneLinks)),
  });
}

/** Hover details for a place: its scenes plus arc moments linked to those scenes. */
export function storyPlaceHover(
  place: StoryPathPlace,
  arcMoments: readonly StoryArcMomentRef[],
): StoryPlaceHover {
  const sceneSet = new Set(place.sceneFiles);
  const arcs = arcMoments.filter((m) => sceneSet.has(m.scenePath));
  return {
    place,
    sceneFiles: place.sceneFiles,
    arcMoments: arcs,
  };
}

/** Scenes in manuscript order that have no landmark or marker yet. */
export function unplacedScenes(
  doc: MapDocument,
  sceneOrder: readonly string[],
): string[] {
  return sceneOrder.filter((path) => {
    const trimmed = trimmedOrNull(path);
    if (trimmed === null) return false;
    return placesForScene(doc, trimmed).length === 0;
  });
}

export function appendSceneLinks(
  existing: Maybe<readonly MapSceneLink[]>,
  files: readonly string[],
): MapSceneLink[] {
  const seen = new Set(sceneLinkFiles(existing));
  const next: MapSceneLink[] = isJust(existing) ? [...existing.value] : [];
  for (const file of files) {
    const trimmed = trimmedOrNull(file);
    if (trimmed === null || seen.has(trimmed)) continue;
    seen.add(trimmed);
    next.push({ file: trimmed });
  }
  return next;
}

export function removeSceneLinks(
  existing: Maybe<readonly MapSceneLink[]>,
  files: readonly string[],
): MapSceneLink[] {
  if (isJust(existing) === false) return [];
  const remove = new Set<string>();
  for (const file of files) {
    const trimmed = trimmedOrNull(file);
    if (trimmed !== null) remove.add(trimmed);
  }
  if (remove.size === 0) return [...existing.value];
  return existing.value.filter((link) => {
    const file = trimmedOrNull(link.file);
    return file === null || remove.has(file) === false;
  });
}

export type StoryPlaceRef = {
  readonly kind: StoryPathPlaceKind;
  readonly id: string;
};

export function createStoryMarker(params: {
  id: string;
  x: number;
  y: number;
  sceneFiles: readonly string[];
}): MapStoryMarker {
  const sceneLinks = appendSceneLinks(nothing, params.sceneFiles);
  return {
    id: params.id,
    x: params.x,
    y: params.y,
    ...(sceneLinks.length > 0 ? { sceneLinks } : {}),
  };
}

/** Hit-test free markers in world space (screen radius / zoom). */
export function findStoryMarkerAt(
  doc: MapDocument,
  world: { x: number; y: number },
  radiusWorld: number,
): Maybe<MapStoryMarker> {
  let best: Maybe<{ marker: MapStoryMarker; dist: number }> = nothing;
  for (const marker of doc.storyMarkers.toReversed()) {
    const dist = Math.hypot(world.x - marker.x, world.y - marker.y);
    if (dist > radiusWorld) continue;
    if (isJust(best) && dist >= best.value.dist) continue;
    best = just({ marker, dist });
  }
  return isJust(best) ? just(best.value.marker) : nothing;
}
